// WIP-011 (design §2.3, §2.4) — pure layout math for the projection wall,
// kept out of the component so it can be unit-tested at the sizes the design
// names explicitly: n=0, n=3, n=77, and the catalogue's max of 12 options.
// Nothing here touches the DOM.

/** Up to 6 options: one column. 7–12: two, split column-major (top-to-bottom,
 * then wrap) so the reading order stays A, B, C… down the first column before
 * continuing into the second — never re-sorted by count (design §2.2). */
export function columnize<T>(options: T[]): T[][] {
  if (options.length <= 6) return [options];
  const perColumn = Math.ceil(options.length / 2);
  return [options.slice(0, perColumn), options.slice(perColumn)];
}

/** Bar row height in vh, capped so a short list doesn't produce absurdly tall
 * bars and a long one still fits: min(9vh, 70vh / rows-in-this-column). */
export function barHeightVh(rowsInColumn: number): number {
  if (rowsInColumn <= 0) return 9;
  return Math.min(9, 70 / rowsInColumn);
}

/** 7–12 options step the label size down one notch (design §2.4 table). */
export function optionLabelVh(totalOptions: number): number {
  return totalOptions > 6 ? 3.5 : 4.5;
}

/** Percent is only meaningful once there is enough of a sample to read as
 * more than noise (design §2.2/§2.3 — "33% at n=3 reads as precision that is
 * not there"). Null means "do not render a percent". */
export function percentOf(count: number, total: number): number | null {
  if (total < 10) return null;
  return Math.round((count / total) * 100);
}

/** Bar width is proportional to the LARGEST count on screen, never to the
 * respondent total — n=3 with counts {A:2, B:1} must still read as two
 * clearly different bars, not two slivers of a bar sized for 77 (design
 * §2.3). Zero counts (n=0) all render as empty tracks. */
export function barWidthPercent(count: number, maxCount: number): number {
  if (maxCount <= 0) return 0;
  return Math.max(0, Math.min(100, (count / maxCount) * 100));
}

/** Sums choice tallies keyed by option index (as strings, matching the
 * backend's `Record<string, number>`) into an array aligned with `options`,
 * so a shown-but-never-picked option still renders its (empty) bar. */
export function countsByOptionIndex(choices: Record<string, number> | undefined, optionCount: number): number[] {
  const counts = new Array(optionCount).fill(0);
  if (!choices) return counts;
  for (const [key, value] of Object.entries(choices)) {
    const idx = Number(key);
    if (Number.isInteger(idx) && idx >= 0 && idx < optionCount) counts[idx] = value;
  }
  return counts;
}
